import { useEffect, useState } from 'react';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { Box, Typography, styled, InputAdornment } from '@mui/material';
import Dialog, { DialogPrimaryButton } from './Dialog';
import DateField from './DateField';
import InputField from './InputField';
import { PLATFORMS, SERVICE_OPTIONS } from '../constants/service';

const ServiceSwitchWrapper = styled(Box)({
  width: 'fit-content',
  padding: 1,
  borderRadius: 20,
  background: 'linear-gradient(190deg, #B9FF66 0%, #5a6b3a 75%, #2a3320 100%)',
  height: 42,
  boxSizing: 'border-box',
  marginBottom: 30,
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
});

const ServiceSwitchContainer = styled(Box)({
  display: 'flex',
  borderRadius: 20,
  padding: 4,
  background: 'linear-gradient(to bottom, #3D4537, #3E4638), #3A4033',
});

const ServiceSegment = styled(Box, {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ $selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 16px',
  borderRadius: 20,
  height: 32,
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: $selected ? 900 : 500,
  letterSpacing: 0.06,
  textTransform: 'uppercase',
  color: $selected ? '#141414' : '#FFFFFF',
  backgroundColor: $selected ? '#B4FF48' : 'transparent',
  border: $selected ? '1px solid #B4FF48' : 'none',
  boxShadow: $selected
    ? 'inset -1px -1px 0 rgba(255,255,255,0.15), 1px 1px 2px rgba(0,0,0,0.15)'
    : 'none',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    color: $selected ? '#141414' : '#FFFFFF',
  },
}));

const FieldWrapper = styled(Box)({
  marginBottom: 30,
  width: '100%',
});

const AddBalanceDialog = ({ open, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(PLATFORMS.heetch);
  const [weekAnchorDate, setWeekAnchorDate] = useState(null);

  useEffect(() => {
    if (open) {
      setWeekAnchorDate(new Date());
      setSelectedPlatform(PLATFORMS.heetch);
      setAmount('');
    }
  }, [open]);

  const handleClose = () => {
    setAmount('');
    setSelectedPlatform(PLATFORMS.heetch);
    setWeekAnchorDate(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!weekAnchorDate || !amount) return;

    onSubmit?.({
      amount: Number(amount),
      platform: selectedPlatform,
      week: getISOWeek(weekAnchorDate),
      year: getISOWeekYear(weekAnchorDate),
    });
    handleClose();
  };

  const canSubmit = Boolean(weekAnchorDate) && Number(amount) > 0;

  return (
    <Dialog open={open} onClose={handleClose} title="AJOUTER UN SOLDE">
      <ServiceSwitchWrapper>
        <ServiceSwitchContainer>
          {SERVICE_OPTIONS.map(({ key, label }) => (
            <ServiceSegment
              key={key}
              $selected={selectedPlatform === key}
              onClick={() => setSelectedPlatform(key)}
            >
              {label}
            </ServiceSegment>
          ))}
        </ServiceSwitchContainer>
      </ServiceSwitchWrapper>

      <FieldWrapper>
        <DateField
          label="Semaine"
          placeholder="Sélectionner une date"
          value={weekAnchorDate}
          onChange={setWeekAnchorDate}
          dateFormat="medium"
          highlightIsoWeek
          sx={{
            width: '100%',
            '& > div': {
              width: '100%',
              maxWidth: '100%',
            },
          }}
        />
      </FieldWrapper>

      <FieldWrapper>
        <InputField
          label="Montant"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              MozAppearance: 'textfield',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ color: '#ACACAC' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>€</Typography>
              </InputAdornment>
            ),
          }}
        />
      </FieldWrapper>
      <DialogPrimaryButton variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
        AJOUTER
      </DialogPrimaryButton>
    </Dialog>
  );
};

export default AddBalanceDialog;
